import {
  Cart,
  CartDiscount,
  CartDiscountReference,
  CartDiscountShippingCostTarget,
  CartDiscountValueAbsolute,
  CartDiscountValueGiftLineItem,
  CartDiscountValueRelative,
  DiscountCode,
  LineItem,
  Order,
} from '@commercetools/platform-sdk';
import { cents, from, fromCents } from '@nuts/auto-delivery-sdk/dist/utils/money';
import uniq from 'lodash/uniq';
import type { Simplify } from 'type-fest';

import { isDefined } from '@/utils/isDefined';
import { Money } from '@/utils/money';

interface NutsStandardCartDiscountFields {
  readonly belowThresholdMessage?: string;
  readonly cartConditionsMetMessage?: string;
  readonly flashMessage?: string;
  readonly landingPagePath?: string;
}

export interface CartDiscountMaybeStandardFields extends CartDiscount {
  custom?: CartDiscount['custom'] & {
    fields: Simplify<NutsStandardCartDiscountFields>;
  };
}

export const ALL_CARTS_WITHOUT_RESTRICTION_PREDICATE = '1 = 1';
/**
 * Matches the predicate for tag reference targeting cart discounts
 * @example
 * /lineItemCount(attributes.tagReferences contains "tag-1") >= 1/
 * /lineItemCount(attributes.tagReferences contains "tag-1" and attributes.tagReferences contains "tag-2") >= 4/
 */
export const TAG_REFERENCE_DISCOUNT_PREDICATE_PATTERN =
  /lineItemCount\(.*?attributes\.tagReferences\s+contains\s+"[^"]+".*?\)/;

/**
 * Matches and extracts tag references from a cart discount predicate string.
 * The predicate itself is expected to be related to line items.
 *
 * @see {@link TAG_REFERENCE_DISCOUNT_PREDICATE_PATTERN}
 */
export const TAG_REFERENCE_EXTRACT_PATTERN = /attributes\.tagReferences\s+contains\s+"([^"]+)"/g;

export function getAmountToActivateDiscount(
  cartPredicate: CartDiscount['cartPredicate'],
  cartTotal: Money,
) {
  const nothingRemaining = from(0);
  if (cartPredicate === ALL_CARTS_WITHOUT_RESTRICTION_PREDICATE) {
    return { amountRemaining: nothingRemaining, threshold: from(0) };
  }
  if (!/totalPrice >/.test(cartPredicate)) return undefined;

  const thresholdMatcher = /^totalPrice >=? "(?<thresholdString>\d+\.\d{2}) USD"$/;
  const match = thresholdMatcher.exec(cartPredicate);
  if (!match?.groups) return undefined;

  const threshold = from(Number(match.groups.thresholdString));
  const neededForThreshold = Money.subtract(threshold, cartTotal);
  const centsNeeded = cents(neededForThreshold);
  let amountRemaining;
  if (centsNeeded === 0 && cartPredicate.includes('totalPrice > ')) {
    amountRemaining = fromCents(1);
  } else {
    amountRemaining = Money.max(neededForThreshold, nothingRemaining);
  }
  return { amountRemaining, threshold };
}

export function getAppliedCartDiscountIds(lineItems: LineItem[]): string[] {
  return uniq(
    lineItems.flatMap((lineItem) =>
      lineItem.discountedPricePerQuantity.flatMap(({ discountedPrice }) =>
        discountedPrice.includedDiscounts.map((portion) => portion.discount.id),
      ),
    ),
  );
}

export function getCartDiscountCodes(cart?: Cart | Order, matchesCart?: boolean): DiscountCode[] {
  const refs = cart?.discountCodes?.filter((d) => {
    if (matchesCart) return d.state === 'MatchesCart';
    if (matchesCart === false) return d.state !== 'MatchesCart';
    return true;
  });
  return refs?.map((d) => d.discountCode.obj).filter(isDefined) ?? [];
}

export function getExpandedCartDiscounts(
  cartDiscountReferences?: CartDiscountReference[],
): CartDiscountMaybeStandardFields[];
export function getExpandedCartDiscounts(
  discountCode?: DiscountCode,
): CartDiscountMaybeStandardFields[];
export function getExpandedCartDiscounts(
  discountCodeOrCartDiscountReferences?: DiscountCode | CartDiscountReference[],
): CartDiscountMaybeStandardFields[] {
  if (!discountCodeOrCartDiscountReferences) return [];
  const cartDiscounts = Array.isArray(discountCodeOrCartDiscountReferences)
    ? discountCodeOrCartDiscountReferences
    : discountCodeOrCartDiscountReferences.cartDiscounts;
  return cartDiscounts.map((d) => d.obj).filter(isDefined);
}

export function getCartDiscountsForMessages(
  cart: Cart | Order | undefined,
  options?: { mustMatchCart?: boolean },
): CartDiscountMaybeStandardFields[] {
  const [matchesCart] = getCartDiscountCodes(cart, true);
  const [doesNotMatchCart] = options?.mustMatchCart ? [] : getCartDiscountCodes(cart, false);

  const discountCode: DiscountCode | undefined = matchesCart ?? doesNotMatchCart;

  return getExpandedCartDiscounts(discountCode);
}

export const SHIPPING_CART_DISCOUNT_KEY = 'shippingCartDiscount';
export function getShippingCartDiscount(cart?: Cart | Order) {
  const cartDiscounts = getCartDiscountCodes(cart).flatMap(getExpandedCartDiscounts);
  return cartDiscounts.find(isShippingDiscount);
}

export function isAbsoluteDiscount(discount: CartDiscount): discount is CartDiscount & {
  value: CartDiscountValueAbsolute;
} {
  return discount.value.type === 'absolute';
}
export function isGiftLineItemDiscount(discount: CartDiscount): discount is CartDiscount & {
  value: CartDiscountValueGiftLineItem;
} {
  return discount.value.type === 'giftLineItem';
}
export function isRelativeDiscount(
  discount: CartDiscount,
): discount is CartDiscount & { value: CartDiscountValueRelative } {
  return discount.value.type === 'relative';
}
export function isShippingDiscount(
  discount: CartDiscount,
): discount is CartDiscount & { target: CartDiscountShippingCostTarget } {
  return (
    discount.custom?.type.obj?.key === SHIPPING_CART_DISCOUNT_KEY &&
    discount.target?.type === 'shipping'
  );
}

export function isCartDiscountApplied(
  cart: Cart | Order | undefined,
  discount: CartDiscount,
  mustMatchCart?: boolean,
): boolean {
  return (
    cart?.discountCodes?.some((dc) => {
      if (mustMatchCart === true && dc.state !== 'MatchesCart') return false;
      return dc.discountCode.obj?.cartDiscounts?.some((ref) => ref.obj?.id === discount.id);
    }) ?? false
  );
}
